import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { Token } from "../interface/Token";
import { usePostLoginMutation, usePostUserTokenMutation } from "./CallTPApi";
import { useGetPersonWhoamiDataMutation } from "./CallTPApi";
import { setAlertMsgStore } from "./alertMsgSlisce";
import { setRolesStore } from "./rolesSlice";
import { setFeatureStore } from "./featureSlice";
import { setTokenInStore } from "./tokenSlice";
import { setTokenAuthenticationInStore } from "./tokenAuthenticationSlice";
import { setDistributorInStore } from "./distributorSlice";
import { setTenantNull, setTenantStore } from "./tenantSlice";
import { Tenant } from "../interface/Tenant";
import { setWhoamitStore } from "./whoamiTPSlice";
import { setOrderInStore } from "./orderSlice";

// Константы
const STORAGE_KEYS = {
    TOKEN: 'token',
    TOKEN_AUTH: 'tokenAuthentication',
    TENANT_ID: 'tenantId',
    TENANT_DATA: 'tenantData',
    ORDER: 'order',
    SESSION_ID: 'sessionId'
} as const;

const TOKEN_REFRESH_BUFFER = 5 * 60; // 5 минут в секундах до истечения токена
const TOKEN_CHECK_INTERVAL = 60 * 1000; // Проверка каждую минуту

// Типы для таймеров (браузерные альтернативы NodeJS.Timeout)
type TimerHandle = ReturnType<typeof setTimeout> | null;
type IntervalHandle = ReturnType<typeof setInterval> | null;

export const useTokenTP = () => {
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const token = useSelector((state: any) => state.token?.value) as Token;
    const [isAuthentication, setIsAuthentication] = useState(false);

    // API mutations
    const [postUserToken] = usePostUserTokenMutation();
    const [postLogin] = usePostLoginMutation();
    const [getPersonWhoamiData] = useGetPersonWhoamiDataMutation();

    // Рефы для таймеров с правильной инициализацией
    const refreshTimeoutRef = useRef<TimerHandle>(null);
    const checkIntervalRef = useRef<IntervalHandle>(null);
    const isRefreshingRef = useRef<boolean>(false);

    // Вспомогательные функции для localStorage
    const clearStorage = useCallback((keys: string[]) => {
        keys.forEach(key => localStorage.removeItem(key));
    }, []);

    const setStorageItem = useCallback((key: string, value: string) => {
        localStorage.setItem(key, value);
    }, []);

    const getStorageItem = useCallback((key: string) => {
        return localStorage.getItem(key);
    }, []);

    // Получение информации о токене
    const getTokenInfo = useCallback((tokenString: string) => {
        try {
            const decoded = jwtDecode(tokenString) as any;
            const expToken = decoded.exp;
            const now = Math.floor(Date.now() / 1000);

            if (!expToken) return null;

            return {
                decoded,
                expTimestamp: expToken,
                timeUntilExp: expToken - now,
                shouldRefresh: (expToken - now) <= TOKEN_REFRESH_BUFFER,
                isExpired: now >= expToken
            };
        } catch (error) {
            console.error('Token decoding error:', error);
            return null;
        }
    }, []);

    // Очистка всех таймеров
    const clearAllTimers = useCallback(() => {
        if (refreshTimeoutRef.current) {
            clearTimeout(refreshTimeoutRef.current);
            refreshTimeoutRef.current = null;
        }
        if (checkIntervalRef.current) {
            clearInterval(checkIntervalRef.current);
            checkIntervalRef.current = null;
        }
    }, []);

    // Выход из системы
    const logOutTP = useCallback((reload = true) => {
        console.log('Logging out...');

        // Очищаем таймеры
        clearAllTimers();

        // Очищаем хранилище
        clearStorage(Object.values(STORAGE_KEYS));

        // Сброс состояния Redux
        dispatch(setOrderInStore(null as any));
        dispatch(setTokenInStore(null as any));
        dispatch(setTenantNull(null as any));
        dispatch(setTokenAuthenticationInStore(null));
        dispatch(setRolesStore(null as any));

        isRefreshingRef.current = false;

        if (reload) {
            window.location.reload();
        }
    }, [dispatch, clearStorage, clearAllTimers]);

    // Проверка валидности токена
    const checkTokenExp = useCallback((tokenString: string): boolean => {
        if (!tokenString) {
            clearStorage([STORAGE_KEYS.TOKEN, STORAGE_KEYS.TENANT_ID]);
            return false;
        }

        try {
            const tokenInfo = getTokenInfo(tokenString);
            if (!tokenInfo || tokenInfo.isExpired) {
                logOutTP(false);
                return false;
            }

            // Обработка ролей и фич
            const roles = tokenInfo.decoded?.role;
            const features = tokenInfo.decoded?.feature;

            if (roles) {
                const rolesArray = Array.isArray(roles) ? roles : [roles.toString()];
                dispatch(setRolesStore(rolesArray as any));
                dispatch(setFeatureStore(rolesArray as any));
            }

            if (features) {
                if (!roles) {
                    dispatch(setRolesStore(features));
                }
                dispatch(setFeatureStore(features));
            }

            return true;
        } catch (error) {
            console.error('Token validation error:', error);
            return false;
        }
    }, [getTokenInfo, dispatch, clearStorage, logOutTP]);

    // Базовая установка токена (без таймеров)
    const setTokenBase = useCallback((tokenString: string) => {
        if (!tokenString || !checkTokenExp(tokenString)) return;

        setStorageItem(STORAGE_KEYS.TOKEN, tokenString);
        dispatch(setTokenInStore({ value: tokenString }));
    }, [checkTokenExp, dispatch, setStorageItem]);

    const setTokenAuthenticationTP = useCallback((tokenString: string) => {
        if (!tokenString || !checkTokenExp(tokenString)) return;

        setStorageItem(STORAGE_KEYS.TOKEN_AUTH, tokenString);
        dispatch(setTokenAuthenticationInStore({ value: tokenString }));
    }, [checkTokenExp, dispatch, setStorageItem]);

    // Обновление токена - объявляем ДО функций таймеров
    const refreshToken = useCallback(async () => {
        if (isRefreshingRef.current) {
            console.log('Refresh already in progress');
            return;
        }

        isRefreshingRef.current = true;
        console.log('Starting token refresh...');

        try {
            const res = await postUserToken(null) as any;

            if (!res.error && res.data?.token) {
                console.log('Token refreshed successfully');
                // Используем setTokenBase чтобы избежать циклической зависимости
                setTokenBase(res.data.token);

                // Настраиваем таймеры для нового токена вручную
                const tokenInfo = getTokenInfo(res.data.token);
                if (tokenInfo) {
                    const refreshTime = (tokenInfo.timeUntilExp - TOKEN_REFRESH_BUFFER) * 1000;
                    if (refreshTime > 0) {
                        if (refreshTimeoutRef.current) {
                            clearTimeout(refreshTimeoutRef.current);
                            refreshTimeoutRef.current = null;
                        }
                        refreshTimeoutRef.current = setTimeout(() => {
                            console.log('Precise refresh timer triggered');
                            refreshToken();
                        }, refreshTime);
                    }
                }
            } else {
                console.error('Token refresh failed:', res.error);
                dispatch(setAlertMsgStore({
                    variant: 'warning',
                    title: "Ошибка авторизации, попробуйте еще раз"
                } as any));
                logOutTP();
            }
        } catch (error) {
            console.error('Token refresh error:', error);
            logOutTP();
        } finally {
            isRefreshingRef.current = false;
        }
    }, [postUserToken, setTokenBase, dispatch, logOutTP, getTokenInfo]);

    // Проверка токена на необходимость обновления
    const checkTokenForRefresh = useCallback(async () => {
        const currentToken = token || getStorageItem(STORAGE_KEYS.TOKEN);

        if (!currentToken || isRefreshingRef.current) return;

        const tokenInfo = getTokenInfo(currentToken as any);
        if (!tokenInfo) {
            logOutTP(false);
            return;
        }

        if (tokenInfo.isExpired) {
            console.log('Token expired, logging out');
            logOutTP(false);
            return;
        }

        if (tokenInfo.shouldRefresh && !isRefreshingRef.current) {
            console.log('Token needs refresh, starting refresh process');
            await refreshToken();
        }
    }, [token, getTokenInfo, refreshToken, logOutTP, getStorageItem]);

    // Настройка периодической проверки токена
    const setupTokenCheckInterval = useCallback(() => {
        // Очищаем предыдущий интервал
        if (checkIntervalRef.current) {
            clearInterval(checkIntervalRef.current);
            checkIntervalRef.current = null;
        }

        // Запускаем новую периодическую проверку
        checkIntervalRef.current = setInterval(() => {
            checkTokenForRefresh();
        }, TOKEN_CHECK_INTERVAL);

        // Первая проверка сразу
        checkTokenForRefresh();
    }, [checkTokenForRefresh]);

    // Настройка точного таймера для обновления
    const setupPreciseRefreshTimer = useCallback((tokenString: string) => {
        const tokenInfo = getTokenInfo(tokenString);
        if (!tokenInfo) return;

        const refreshTime = (tokenInfo.timeUntilExp - TOKEN_REFRESH_BUFFER) * 1000;

        if (refreshTime > 0) {
            // console.log(`Setting up precise token refresh in ${Math.round(refreshTime / 1000 / 60)} minutes`);
            // Очищаем предыдущий таймер
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
                refreshTimeoutRef.current = null;
            }

            refreshTimeoutRef.current = setTimeout(() => {
                console.log('Precise refresh timer triggered');
                refreshToken();
            }, refreshTime);
        }
    }, [getTokenInfo, refreshToken]);

    // Установка токена с таймерами
    const setTokenTP = useCallback((tokenString: string) => {
        if (!tokenString || !checkTokenExp(tokenString)) return;

        setStorageItem(STORAGE_KEYS.TOKEN, tokenString);
        dispatch(setTokenInStore({ value: tokenString }));

        // Настраиваем таймеры для нового токена
        setupPreciseRefreshTimer(tokenString);
        setupTokenCheckInterval();
    }, [checkTokenExp, dispatch, setStorageItem, setupPreciseRefreshTimer, setupTokenCheckInterval]);

    // Работа с дистрибьютором
    const setDistributor = useCallback(async (getToken = false) => {
        const distributorId = searchParams.get("distributorId");
        if (!distributorId) return;

        try {
            const res = await postLogin({ distributorId } as any) as any;

            if (res.data) {
                dispatch(setDistributorInStore({ id: distributorId }));
                dispatch(setTenantStore({ id: res.data.tenantId } as any));

                const tenantData: Tenant = { id: res.data.tenantId } as any;
                setStorageItem(STORAGE_KEYS.TENANT_ID, res.data.tenantId);
                setStorageItem(STORAGE_KEYS.TENANT_DATA, JSON.stringify(tenantData));

                if (getToken && res.data.token) {
                    setTokenTP(res.data.token);
                    setTokenAuthenticationTP(res.data.token);
                }
            } else if (res.error) {
                dispatch(setAlertMsgStore({
                    title: "Нет доступа по ссылке",
                    variant: "danger"
                } as any));
            }
        } catch (error) {
            console.error('Distributor setup error:', error);
        }
    }, [searchParams, postLogin, dispatch, setStorageItem, setTokenTP, setTokenAuthenticationTP]);

    // Загрузка данных пользователя
    const loadUserData = useCallback(async () => {
        if (!token) return;

        try {
            const res = await getPersonWhoamiData() as any;
            if (res.data) {
                dispatch(setWhoamitStore(res.data));
            }
        } catch (error) {
            console.error('User data loading error:', error);
        }
    }, [token, getPersonWhoamiData, dispatch]);

    // Обновление токена при инициализации
    const updateTokenTP = useCallback(async () => {
        setIsAuthentication(true);

        try {
            const tokenAuthentication = getStorageItem(STORAGE_KEYS.TOKEN_AUTH);

            if (tokenAuthentication && checkTokenExp(tokenAuthentication)) {
                setTokenTP(tokenAuthentication);
                setTokenAuthenticationTP(tokenAuthentication);
                await setDistributor();

                const tenantData = getStorageItem(STORAGE_KEYS.TENANT_DATA);
                if (tenantData) {
                    const tenant = JSON.parse(tenantData);
                    if (tenant) {
                        dispatch(setTenantStore(tenant));
                        await refreshToken();
                    }
                }
            } else {
                await setDistributor(true);
            }
        } catch (error) {
            console.error('Token update error:', error);
        } finally {
            setIsAuthentication(false);
        }
    }, [
        getStorageItem, checkTokenExp, setTokenTP, setTokenAuthenticationTP,
        setDistributor, dispatch, refreshToken
    ]);

    // Основной эффект для управления токеном
    useEffect(() => {
        if (!token) return;

        const initializeToken = async () => {
            await loadUserData();
            setupPreciseRefreshTimer(token as any);
            setupTokenCheckInterval();
        };

        initializeToken();

        // Очистка при размонтировании
        return () => {
            clearAllTimers();
        };
    }, [token, loadUserData, setupPreciseRefreshTimer, setupTokenCheckInterval, clearAllTimers]);

    return {
        token,
        setTokenAuthenticationTP,
        setTokenTP,
        logOutTP,
        updateTokenTP,
        isAuthentication,
        setIsAuthentication,
        // Дополнительные методы для управления таймерами
        stopTimers: clearAllTimers,
        startTokenCheck: setupTokenCheckInterval
    };

};